/**
 * useUploadBenchmarks — POST /v1/admin/benchmarks/{performance_year}
 *
 * Multipart upload of the CMS benchmark CSV for one performance year.
 * Replace-all on the backend: existing rows for the year are deleted, then
 * the parsed CSV is inserted. The success response includes
 * `rows_parsed / rows_inserted / rows_updated / filename` for the toast.
 *
 * Per OpenAPI, the body is `multipart/form-data` with a `file` field.
 */
import { useMutation } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { BenchmarkUploadResponse } from "@/api/models";

export interface UploadBenchmarksVariables {
  year: number;
  file: File;
}

export function useUploadBenchmarks() {
  return useMutation({
    mutationFn: async ({ year, file }: UploadBenchmarksVariables): Promise<BenchmarkUploadResponse> => {
      const form = new FormData();
      form.append("file", file);
      const { data } = await getApiClient().post<BenchmarkUploadResponse>(
        `/v1/admin/benchmarks/${year}`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          // Benchmark uploads can be many rows; bump the timeout from the
          // default 30s.
          timeout: 120_000,
        },
      );
      return data;
    },
  });
}
